import React, { JSX } from "react";

type PerformanceData = {
  fundLaunched: boolean;
  fundFall: number | null;
  benchmarkFall: number | null;
  fundDays: number | null;
  benchmarkDays: number | null;
};

const RiskPerformanceCard = ({
  fundLaunched,
  fundFall,
  benchmarkFall,
  fundDays,
  benchmarkDays,
}: PerformanceData) => {
  const icons = {
    down: (
      <svg
        width="24"
        height="25"
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21.9991 17.5464L13.5995 9.1468L10.3997 13.9466L4 7.54688"
          stroke="#D50000"
          strokeWidth="1.875"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18 17.5469H21.9998V13.5469"
          stroke="#D50000"
          strokeWidth="1.875"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    up: (
      <svg
        width="24"
        height="25"
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M21.9991 7.54688L13.5995 15.9469L10.3997 11.1469L4 17.5469"
          stroke="#1F840D"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18 7.54688H21.9998V11.5469"
          stroke="#1F840D"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    perf: (
      <svg
        width="24"
        height="25"
        viewBox="0 0 24 25"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5 17.5469C5 13.6809 8.13401 10.5469 12 10.5469M16.4999 13.0469L11.9999 17.5469M22 17.5469C22 12.024 17.5228 7.54688 12 7.54688C6.47715 7.54688 2 12.024 2 17.5469M13 17.5469C13 18.0992 12.5523 18.5469 12 18.5469C11.4477 18.5469 11 18.0992 11 17.5469C11 16.9946 11.4477 16.5469 12 16.5469C12.5523 16.5469 13 16.9946 13 17.5469Z"
          stroke="#1A51AA"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  };

  const getColorClass = (value: number | null | undefined) => {
    if (value === null || value === undefined) return "text-[#202020A6]";
    return value < 0 ? "text-[#D50000]" : "text-[#1F840D]";
  };

  const getPerformanceTag = (label: string): JSX.Element => {
    const base = "px-2.5 py-1 rounded-full whitespace-nowrap text-2xs";

    switch (label) {
      case "excellent-performed":
        return (
          <span className={`${base} bg-green-100 text-green-700`}>
            Excellent performed
          </span>
        );
      case "fast-recovery":
        return (
          <span className={`${base} bg-[#D2E1F8] text-primaryBlue`}>
            Fast recovery
          </span>
        );
      case "slow-recovery":
        return (
          <span className={`${base} bg-red-100 text-red-700`}>
            Slow recovery
          </span>
        );
      case "not-recovered":
        return (
          <span className={`${base} bg-red-100 text-red-700`}>
            Not Recovered
          </span>
        );
      default:
        return <span className="text-gray-500">–</span>;
    }
  };

  function getRelativeRecovery(
    fundFall: number | null,
    benchmarkFall: number | null,
    fundDays: number | null,
    benchmarkDays: number | null,
  ) {
    const fundRecovered = fundDays != null && fundFall != null && fundDays >= 0;
    const benchmarkRecovered =
      benchmarkDays != null && benchmarkFall != null && benchmarkDays >= 0;

    if (fundRecovered && benchmarkRecovered) {
      if (fundDays < benchmarkDays && fundFall <= benchmarkFall) {
        return { fund: "excellent-performed", benchmark: "slow-recovery" };
      }
      if (fundDays < benchmarkDays) {
        return { fund: "fast-recovery", benchmark: "slow-recovery" };
      }
      if (fundDays > benchmarkDays && benchmarkFall <= fundFall) {
        return { fund: "slow-recovery", benchmark: "excellent-performed" };
      }
      if (fundDays > benchmarkDays) {
        return { fund: "slow-recovery", benchmark: "fast-recovery" };
      }
      if (fundFall < benchmarkFall) {
        return { fund: "fast-recovery", benchmark: "slow-recovery" };
      }
      if (fundFall > benchmarkFall) {
        return { fund: "slow-recovery", benchmark: "fast-recovery" };
      }
      return { fund: "fast-recovery", benchmark: "fast-recovery" };
    }

    if (fundRecovered && !benchmarkRecovered) {
      return {
        fund: fundDays === 0 ? "excellent-performed" : "fast-recovery",
        benchmark: "not-recovered",
      };
    }

    if (!fundRecovered && benchmarkRecovered) {
      return {
        fund: "not-recovered",
        benchmark:
          benchmarkDays === 0 ? "excellent-performed" : "fast-recovery",
      };
    }

    return { fund: "not-recovered", benchmark: "not-recovered" };
  }

  const relativePerf = getRelativeRecovery(
    fundFall,
    benchmarkFall,
    fundDays,
    benchmarkDays,
  );

  const cards = [
    {
      title: "Maximum Fall",
      icon: icons.down,
      fundValue: fundLaunched ? `${fundFall?.toFixed(2)}%` : "Not Launched",
      benchmarkValue: `${benchmarkFall?.toFixed(2)}%`,
      fundColor: fundLaunched ? getColorClass(fundFall) : "text-[#202020A6]",
      benchmarkColor: getColorClass(benchmarkFall),
    },
    {
      title: "Recovery",
      icon: icons.up,
      fundValue: fundLaunched
        ? !fundDays || fundDays < 0
          ? "Not Recovered"
          : `${fundDays} days`
        : "Not Launched",
      benchmarkValue: benchmarkDays ? `${benchmarkDays} days` : "Not Recovered",
      fundColor: fundLaunched
        ? !fundDays || fundDays < 0
          ? "text-red-700"
          : "text-[#1F840D]"
        : "Not Launched",
      benchmarkColor: benchmarkDays ? "text-[#1F840D]" : "text-red-700",
    },
    {
      title: "Performance",
      icon: icons.perf,
      fundValue: fundLaunched
        ? getPerformanceTag(relativePerf.fund)
        : "Not Launched",
      benchmarkValue: getPerformanceTag(relativePerf.benchmark),
      fundColor: "",
      benchmarkColor: "",
    },
  ];

  return (
    <div className="flex flex-col sm:flex-row items-center justify-evenly gap-5 overflow-auto sm:pb-3 md:pb-0">
      {cards.map((card, idx) => (
        <div
          key={idx}
          className="rounded-4xl w-full sm:max-w-65 border border-[#E9E9EB] bg-white space-y-5 p-6 shrink-0"
        >
          <div className="flex justify-between items-center gap-2 font-medium text-lg text-primaryBlack">
            {card.title} {card.icon}
          </div>
          <div className="flex justify-between items-center gap-2 text-sm text-[#444444]">
            Fund
            <span className={`${card.fundColor} text-2xs`}>
              {card.fundValue}
            </span>
          </div>
          <div className="flex justify-between items-center gap-2 text-sm text-[#444444]">
            Benchmark
            <span className={`${card.benchmarkColor} text-2xs`}>
              {card.benchmarkValue}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RiskPerformanceCard;
